import { useState } from 'react';
import { useLocation } from 'react-router';
import { joinURL } from 'ufo';
import wretch from 'wretch';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useUserPlatform from '@/hooks/use-user-platform';
import type { DownloadFileType } from '@/models/messages/comm-message';
import { simpleLogger } from '@/services/sentry';
import { BASE_URL, REDIRECT_TO_STORAGE_KEY } from '@/utils/constants';
import { canParseAsUrl, parseUrl } from '@/utils/parse-url';

export default function useDownloadUrl() {
  const { isIOS, isStandalone } = useUserPlatform();
  const location = useLocation();
  const [isLoading, setLoading] = useState(false);
  const { dispatch } = useCommDispatcher();

  const handleDownloadUrl = async (url: Nullable<string>, fileType: DownloadFileType, filename?: string) => {
    if (!url) return;
    setLoading(true);
    if (isIOS && isStandalone) {
      sessionStorage.setItem(
        REDIRECT_TO_STORAGE_KEY,
        joinURL('/', BASE_URL, location.pathname, location.search, location.hash),
      );
    }

    dispatch({
      event: 'download-url',
      data: {
        url,
        filename,
        as: fileType,
      },
    });

    await downloadUrl(url, filename);

    setLoading(false);
  };

  return {
    handleDownloadUrl,
    isLoading,
  };
}

async function downloadUrl(url: Nullable<string>, filename?: string) {
  if (!url || !canParseAsUrl(url)) {
    simpleLogger({
      name: 'DOWNLOAD',
      op: 'download.assert',
      requestAttributes: { url: url || 'null' },
    });
    throw new Error('BAD_DOWNLOAD_URL');
  }

  if (url.startsWith('blob:')) {
    downloadUrlAsLink(url, filename);
  } else if (url.startsWith('http')) {
    const blobUrl = await wretch()
      .url(url)
      .options({
        mode: 'cors',
        responseType: 'blob',
      })
      .get()
      .blob((blob) => URL.createObjectURL(blob))
      .catch((err) => {
        simpleLogger({
          name: 'DOWNLOAD',
          op: 'download.load-blob',
          requestAttributes: { url, error: err },
        });
        return '';
      });

    downloadUrlAsLink(blobUrl || url, filename);
  } else {
    simpleLogger({
      name: 'DOWNLOAD',
      op: 'download.unknown',
      requestAttributes: { url },
    });
    throw new Error('UNKNOWN_DOWNLOAD_URL');
  }
}

function downloadUrlAsLink(url: string, filename?: string) {
  const defaultFilename = parseUrl(url)?.pathname?.split('/').pop();
  let link = document.createElement('a');
  link.download = filename || defaultFilename || 'download';
  link.target = '_blank';
  link.href = url;
  link.innerText = 'Download';
  link.style.visibility = 'hidden';
  link = document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 600);

  return url;
}
